import React, { type JSX } from "react";
import type { ConversationFilters } from "../types";
import {
  TextFilter,
  DraftTextFilter,
  SelectFilter,
  BooleanFilter,
  type OnDraftChange,
  type OnFilterChange,
} from "./FilterInputs";
import type { FilterDraft } from "./filterDraft";
import type { FilterValue } from "./useConversationList";

interface QuickFilter {
  field: keyof ConversationFilters;
  label: string;
  activeClass: string;
  idleClass: string;
  /** Defaults to "the flag is set". */
  isActive?: (filters: ConversationFilters) => boolean;
  /** Value applied on click; defaults to latching the flag on. */
  nextValue?: (filters: ConversationFilters) => FilterValue;
}

const QUICK_FILTERS: QuickFilter[] = [
  {
    field: "hasErrors",
    label: "⚠️ With Errors",
    activeClass: "bg-red-600 text-white",
    idleClass: "bg-red-100 text-red-700 hover:bg-red-200",
  },
  {
    field: "hasPositiveFeedback",
    label: "👍 Positive Feedback",
    activeClass: "bg-green-600 text-white",
    idleClass: "bg-green-100 text-green-700 hover:bg-green-200",
  },
  {
    field: "hasNegativeFeedback",
    label: "👎 Negative Feedback",
    activeClass: "bg-red-600 text-white",
    idleClass: "bg-red-100 text-red-700 hover:bg-red-200",
  },
  {
    field: "hitLimits",
    label: "🚫 Rate Limited",
    activeClass: "bg-orange-600 text-white",
    idleClass: "bg-orange-100 text-orange-700 hover:bg-orange-200",
  },
  {
    // 0-turn sessions are hidden by default, so this chip reveals rather than
    // narrows: it reads as on whenever those rows are part of the result set.
    field: "hasTurns",
    label: "👁️ Show 0-turn",
    activeClass: "bg-gray-700 text-white",
    idleClass: "bg-gray-200 text-gray-700 hover:bg-gray-300",
    isActive: (filters) => filters.hasTurns !== true,
    nextValue: (filters) => (filters.hasTurns === true ? undefined : true),
  },
];

/** One-click filters for the states most often looked for. */
function QuickFilters({
  filters,
  onFilterChange,
}: {
  filters: ConversationFilters;
  onFilterChange: OnFilterChange;
}): JSX.Element {
  return (
    <div className="mb-4">
      <div className="flex flex-wrap gap-2">
        {QUICK_FILTERS.map((quick) => (
          <button
            key={quick.field}
            // Not `?? true`: nextValue returning undefined is a real value (clear the filter).
            onClick={() => onFilterChange(quick.field, quick.nextValue ? quick.nextValue(filters) : true)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              (quick.isActive?.(filters) ?? filters[quick.field] === true)
                ? quick.activeClass
                : quick.idleClass
            }`}
          >
            {quick.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const ENVIRONMENT_OPTIONS = [
  { value: "", label: "All Environments" },
  { value: "prod", label: "Production" },
  { value: "dev", label: "Development" },
  { value: "staging", label: "Staging" },
  { value: "test", label: "Test" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "webchat,directline", label: "Web" },
  { value: "msteams", label: "Teams" },
  { value: "claude-ai", label: "Claude.ai" },
  { value: "claude-cowork", label: "Claude cowork" },
  { value: "claude-code", label: "Claude Code" },
  { value: "claude-ai,claude-cowork,claude-code", label: "All Claude (MCP)" },
];

/** The boolean flag filters, which all share the same shape. */
function BooleanFilters({
  filters,
  onFilterChange,
}: {
  filters: ConversationFilters;
  onFilterChange: OnFilterChange;
}): JSX.Element {
  return (
    <>
      <BooleanFilter
        label="Errors"
        field="hasErrors"
        value={filters.hasErrors}
        trueLabel="With Errors ⚠️"
        falseLabel="No Errors"
        onFilterChange={onFilterChange}
      />
      <BooleanFilter
        label="Positive Feedback"
        field="hasPositiveFeedback"
        value={filters.hasPositiveFeedback}
        trueLabel="With Thumbs Up 👍"
        falseLabel="No Thumbs Up"
        onFilterChange={onFilterChange}
      />
      <BooleanFilter
        label="Negative Feedback"
        field="hasNegativeFeedback"
        value={filters.hasNegativeFeedback}
        trueLabel="With Thumbs Down 👎"
        falseLabel="No Thumbs Down"
        onFilterChange={onFilterChange}
      />
      <BooleanFilter
        label="Rate Limits"
        field="hitLimits"
        value={filters.hitLimits}
        trueLabel="Hit Rate Limits 🚫"
        falseLabel="No Rate Limits"
        onFilterChange={onFilterChange}
      />
      <BooleanFilter
        label="Turns"
        field="hasTurns"
        value={filters.hasTurns}
        trueLabel="With Turns 💬"
        falseLabel="0-turn Only"
        onFilterChange={onFilterChange}
      />
    </>
  );
}

/** Who and where: free-text search, environment, source and date range. */
function SearchFilters({
  filters,
  draft,
  onFilterChange,
  onDraftChange,
}: {
  filters: ConversationFilters;
  draft: FilterDraft;
  onFilterChange: OnFilterChange;
  onDraftChange: OnDraftChange;
}): JSX.Element {
  return (
    <>
      <DraftTextFilter
        label="Search"
        field="search"
        value={draft.search}
        placeholder="Conversation ID or Username"
        onDraftChange={onDraftChange}
      />
      <DraftTextFilter
        label="Username"
        field="username"
        value={draft.username}
        placeholder="Filter by username"
        onDraftChange={onDraftChange}
      />
      <SelectFilter
        label="Environment"
        field="environment"
        value={filters.environment || ""}
        options={ENVIRONMENT_OPTIONS}
        onFilterChange={onFilterChange}
      />
      <SelectFilter
        label="Source"
        field="channel"
        value={filters.channel || ""}
        options={SOURCE_OPTIONS}
        onFilterChange={onFilterChange}
      />
      <TextFilter
        label="From Date"
        field="dateFrom"
        type="date"
        value={filters.dateFrom || ""}
        onFilterChange={onFilterChange}
      />
      <TextFilter
        label="To Date"
        field="dateTo"
        type="date"
        value={filters.dateTo || ""}
        onFilterChange={onFilterChange}
      />
    </>
  );
}

/** Conversation duration bounds. */
function DurationFilters({
  draft,
  onDraftChange,
}: {
  draft: FilterDraft;
  onDraftChange: OnDraftChange;
}): JSX.Element {
  return (
    <>
      <DraftTextFilter
        label="Min Time (ms)"
        field="minTimeMs"
        type="number"
        value={draft.minTimeMs}
        placeholder="e.g. 1000"
        onDraftChange={onDraftChange}
      />
      <DraftTextFilter
        label="Max Time (ms)"
        field="maxTimeMs"
        type="number"
        value={draft.maxTimeMs}
        placeholder="e.g. 30000"
        onDraftChange={onDraftChange}
      />
    </>
  );
}

/**
 * Everything that narrows the conversation list.
 *
 * Stays mounted while a request is in flight. Replacing it with a spinner
 * unmounted the inputs and dropped focus after every keystroke.
 */
export default function ConversationFiltersPanel({
  filters,
  draft,
  onFilterChange,
  onDraftChange,
  onClearFilters,
}: {
  filters: ConversationFilters;
  draft: FilterDraft;
  onFilterChange: OnFilterChange;
  onDraftChange: OnDraftChange;
  onClearFilters: () => void;
}): JSX.Element {
  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>

      <QuickFilters filters={filters} onFilterChange={onFilterChange} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        <SearchFilters
          filters={filters}
          draft={draft}
          onFilterChange={onFilterChange}
          onDraftChange={onDraftChange}
        />
        <BooleanFilters filters={filters} onFilterChange={onFilterChange} />
        <DurationFilters draft={draft} onDraftChange={onDraftChange} />

        <div className="flex items-end">
          <button
            onClick={onClearFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}
